import { AuthProvider, UserIdentity } from 'ra-core'
import { getAccessToken, getIsAuthenticated, getUser, isRedirecting, logout } from '../oidc'
import authToken from '../utils/authToken'
import { getScope } from '../utils/getScope'
import { tokenHasAdminRole } from '../utils/tokenRole'

interface Permissions {
  scopes: string[]
}

const clearLocalStorage = (): void => {
  authToken.deleteToken()
  authToken.deleteOrganization()
  localStorage.removeItem('nickname')
}

const authProvider = (): AuthProvider => ({
  login: (): Promise<void> => Promise.resolve(),
  logout: async (): Promise<void> => {
    const isAuthenticated = await getIsAuthenticated()
    clearLocalStorage()
    if (isAuthenticated) {
      await logout()
      // Block react-admin's in-SPA navigate until KC's top-level redirect tears down the page; hard-reload as fallback.
      await new Promise<void>((resolve) => {
        setTimeout(() => {
          window.location.href = window.location.origin + '/login'
          resolve()
        }, 3000)
      })
    }
  },
  checkError: async (error: { status: number }): Promise<void> => {
    if (error?.status === 412) {
      return Promise.reject()
    }
    if (error?.status === 401) {
      const isAuthenticated = await getIsAuthenticated()
      if (isAuthenticated) void logout()
      clearLocalStorage()
      return Promise.reject()
    }
    if (error?.status === 403) {
      return Promise.reject({ redirectTo: '/unauthorized', logoutUser: false })
    }
    return Promise.resolve()
  },
  checkAuth: async (): Promise<void> => {
    if (isRedirecting()) return Promise.resolve()

    let token = authToken.getToken()
    if (!token) {
      const isAuthenticated = await getIsAuthenticated()
      if (isAuthenticated) {
        await getAccessToken()
        token = authToken.getToken()
      }
    }

    if (!token) {
      return Promise.reject({ message: false })
    }

    if (!tokenHasAdminRole(token)) {
      // Full KC logout (not in-SPA): keeping Layout mounted would 403-loop checkAuth.
      // Flag survives the roundtrip so Login.tsx can explain why.
      sessionStorage.setItem('logoutReason', 'noAdmin')
      clearLocalStorage()
      await logout()
      return Promise.reject({ message: false })
    }

    // Admin with no sa.* scopes — show explicit "contact platform admin" instead of an empty menu.
    if (getScope().length === 0) {
      return Promise.reject({ redirectTo: '/no-permissions', logoutUser: false })
    }

    return Promise.resolve()
  },
  getPermissions: (): Promise<Permissions> => Promise.resolve({ scopes: getScope() }),
  getIdentity: async (): Promise<UserIdentity> => {
    const user = await getUser()
    if (!user) {
      return {
        id: 1,
        fullName: localStorage.getItem('nickname') || ''
      }
    }

    return {
      id: user.sub || '',
      fullName: user.name ?? user.nickname ?? '',
      organizationId: user.org_id,
    }
  },
})

export default authProvider
